import { userQueryOptions } from '@/api/queries/users';
import { PreviewCard } from '@/components/display/preview-card';
import { Notification } from '@/types/notification';
import { imageUrl } from '@/utils/image-url';
import { useQuery } from '@tanstack/react-query';
import { router } from 'expo-router';
import React from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import { FriendRequestButtons, NotificationActions } from './notification-actions';
import { NotificationAvatar } from './notification-avatar';
import { NotificationContent } from './notification-content';
import { getNotificationBadgeBg, getNotificationIcon, getNotificationIconColor, hasCheckInPreview, hasListPreview, hasMenuItemPreview } from './notification-types';

interface NotificationItemProps {
  notification: Notification;
  canRespondToFriendRequest?: boolean;
  onPress?: (notification: Notification) => void;
  onMarkAsRead?: (notificationId: string) => void;
  onAcceptFriendRequest?: (notificationId: string, requestId: string) => void;
  onRejectFriendRequest?: (notificationId: string, requestId: string) => void;
  isProcessingFriendRequest?: boolean;
}

export function NotificationItem({
  notification,
  canRespondToFriendRequest = false,
  onPress,
  onMarkAsRead,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  isProcessingFriendRequest = false,
}: NotificationItemProps) {
  const userAvatar = imageUrl(notification.user?.avatar, 'thumbnail');

  const { data: resolvedUser } = useQuery({
    ...userQueryOptions(notification.user?.id ?? ''),
    enabled: !!notification.user?.id,
  });

  const handlePress = () => {
    if (onMarkAsRead && !notification.read) {
      onMarkAsRead(notification.id);
    }
    if (onPress) {
      onPress(notification);
    }
  };

  const handleUserPress = () => {
    if (!notification.user) return;

    const identifier = resolvedUser?.user?.username || notification.user.username || notification.user.id;
    router.push({
      pathname: '/(root)/profile/[username]',
      params: { username: identifier },
    });
  };

  const iconName = getNotificationIcon(notification.type);
  const iconColor = getNotificationIconColor(notification.type);
  const badgeBg = getNotificationBadgeBg(notification.type);

  return (
    <Pressable
      className={`p-4 mb-2 rounded-xl ${notification.read ? '' : 'bg-surface'}`}
      onPress={handlePress}
    >
      <View className="flex-row">
        <NotificationAvatar
          user={notification.user}
          avatarUrl={userAvatar || null}
          iconName={iconName}
          iconColor={iconColor}
          badgeBg={badgeBg}
          onUserPress={handleUserPress}
        />

        <View className="flex-1">
          <View className="flex-row items-start justify-between">
            <NotificationContent
              notification={notification}
              onUserPress={handleUserPress}
            />
            <View className="items-end">
              <Text className="text-xs mb-1 text-muted-foreground">
                {notification.time}
              </Text>
              <FriendRequestButtons
                notification={notification}
                canRespondToFriendRequest={canRespondToFriendRequest}
                onAcceptFriendRequest={onAcceptFriendRequest}
                onRejectFriendRequest={onRejectFriendRequest}
                isProcessingFriendRequest={isProcessingFriendRequest}
              />
            </View>
          </View>

          {notification.image && (
            <Image
              source={{ uri: notification.image }}
              className="max-w-96 h-48 rounded-lg mt-2"
              resizeMode="cover"
              accessibilityLabel={notification.menuItemName ? `Photo of ${notification.menuItemName}` : 'Notification image'}
            />
          )}

          {hasCheckInPreview(notification) && (
            <PreviewCard
              variant="check_in"
              data={{
                checkInId: notification.referenceId!,
                userName: notification.checkInUserName || notification.user?.name || '',
                userAvatar: notification.checkInUserAvatar,
                menuItemName: notification.menuItemName!,
                businessName: notification.businessName!,
                ratingKey: notification.ratingKey,
                occurredAt: notification.checkInOccurredAt,
                location: notification.businessCity,
              }}
              onPress={handlePress}
            />
          )}

          {hasMenuItemPreview(notification) && (
            <PreviewCard
              variant="menu_item"
              data={{
                menuItemId: notification.referenceId!,
                menuItemName: notification.menuItemName!,
                businessName: notification.businessName!,
                coverPhoto: notification.menuItemCoverPhoto,
                checkInCount: notification.menuItemCheckInCount,
                ratingDistribution: notification.menuItemRatingDistribution,
              }}
              onPress={() => {
                router.push({
                  pathname: '/(root)/menu-item/[id]',
                  params: { id: notification.referenceId! },
                });
              }}
            />
          )}

          {hasListPreview(notification) && (
            <PreviewCard
              variant="list"
              data={{
                listId: notification.listId!,
                listName: notification.listName!,
                coverImage: notification.listCoverImage,
                ownerName: notification.listOwnerName,
                itemCount: notification.listItemCount,
                memberCount: notification.listMemberCount,
              }}
              onPress={() => {
                router.push({
                  pathname: '/(root)/lists/[id]',
                  params: { id: notification.listId! },
                });
              }}
            />
          )}

          <NotificationActions
            notification={notification}
            canRespondToFriendRequest={canRespondToFriendRequest}
          />
        </View>
      </View>

      {!notification.read && (
        <View className="absolute top-4 right-4 w-2 h-2 rounded-full bg-primary" />
      )}
    </Pressable>
  );
}
